/** RiskChecksPanel — Risk Engine verdict + per-check fixes + summary sizing. */
import { CheckCircle2, XCircle, ShieldCheck, ShieldAlert, Wand2, Loader2 } from "lucide-react";
import { PROP } from "@/constants/testIds";

export default function RiskChecksPanel({ verdict, applyingFix, onApplyFix }) {
  return (
    <section className="grid-cell" data-testid={PROP.riskCard} aria-labelledby="risk-panel-title">
      <div className="flex items-center gap-2 mb-3">
        <div className="label-cap" id="risk-panel-title">Deterministic Risk Engine</div>
        {verdict && (
          <span
            className={`chip ${verdict.pass ? "pnl-up" : "pnl-down"} border-current ml-auto`}
            role="status"
            aria-live="polite"
          >
            {verdict.pass ? <><ShieldCheck size={12}/> PASS</> : <><ShieldAlert size={12}/> BLOCK</>}
          </span>
        )}
      </div>
      {!verdict && <div className="text-xs text-muted-foreground">Run risk check to see verdict.</div>}
      {verdict && (
        <div className="space-y-1.5">
          {verdict.checks.map((k, i) => (
            <div key={i} className="py-1.5 border-b border-border/30 last:border-0">
              <div className="flex items-start gap-2">
                {k.pass
                  ? <CheckCircle2 className="pnl-up shrink-0 mt-0.5" size={14} aria-label="Pass"/>
                  : <XCircle className="pnl-down shrink-0 mt-0.5" size={14} aria-label="Fail"/>}
                <div className="text-xs flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-foreground">{k.name}</span>
                    {k.current != null && <span className="text-muted-foreground">· current: <span className="mono">{k.current}</span></span>}
                    {k.required != null && <span className="text-muted-foreground">· required: <span className="mono">{k.required}</span></span>}
                  </div>
                  <div className="text-muted-foreground mt-0.5">{k.message}</div>
                </div>
              </div>
              {!k.pass && k.suggested_fix && (
                <div className="mt-2 ml-6 flex flex-wrap items-center gap-2" data-testid={`risk-fix-${i}`}>
                  <Wand2 size={12} className="text-signal-yellow" aria-hidden="true"/>
                  <span className="text-[11px] text-muted-foreground">Suggested fix:</span>
                  <span className="text-[11px]">{k.suggested_fix.label}</span>
                  {["set_qty", "set_stop", "set_target", "set_entry"].includes(k.suggested_fix.action) && (
                    <button
                      className="btn-outline h-6 !px-2 text-[10px]"
                      disabled={applyingFix}
                      onClick={() => onApplyFix(k.suggested_fix.action,
                        k.suggested_fix.qty ?? k.suggested_fix.stop ?? k.suggested_fix.target ?? k.suggested_fix.entry)}
                    >
                      {applyingFix ? <Loader2 className="animate-spin" size={10}/> : <Wand2 size={10}/>} Apply Fix
                    </button>
                  )}
                  {(k.suggested_fix.alternatives || []).map((alt, ai) => (
                    ["set_qty", "set_stop", "set_target", "set_entry"].includes(alt.action) && (
                      <button
                        key={ai}
                        className="btn-ghost h-6 !px-2 text-[10px] underline underline-offset-2"
                        disabled={applyingFix}
                        onClick={() => onApplyFix(alt.action, alt.qty ?? alt.stop ?? alt.target ?? alt.entry)}
                      >or: {alt.label}</button>
                    )
                  ))}
                </div>
              )}
            </div>
          ))}
          {verdict.sizing && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-border/40 mt-3">
              <div className="p-2 bg-card/40"><div className="label-cap">Order Value</div><div className="mono text-sm">${verdict.sizing.order_value?.toLocaleString?.() ?? "—"}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Max Loss</div><div className="mono text-sm pnl-down">-${verdict.sizing.max_loss}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">R/R</div><div className="mono text-sm">{verdict.sizing.risk_reward}:1</div></div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
